import React from 'react'
import { 
  Box, 
  Paper, 
  Typography, 
  alpha,
  useTheme
} from '@mui/material'
import { styled } from '@mui/material/styles'

const StyledContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: theme.spacing(2),
}))

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
}))

const TotalCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2.5),
  borderRadius: theme.spacing(1.5),
  background: alpha(theme.palette.primary.main, 0.04),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
}))

// Productos envase (id_uso 367)
const ID_USO_ENVASE = 367

const sumarCantidad = (items) => items.reduce((acc, item) => acc + (Number(item.total_cantidad) || 0), 0)

const SeccionProductos = ({ titulo, items }) => {
  if (!items.length) return null

  return (
    <Box mb={2}>
      <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={1}>
        <Typography variant="body2" fontWeight="600" color="textSecondary">
          {titulo}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Subtotal: <b>{sumarCantidad(items)}</b> unidades
        </Typography>
      </Box>
      <StyledContainer>
        {items.map((item, index) => (
          <MetricCard key={index} elevation={0}>
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography variant="body2" color="textSecondary" noWrap>
                {item.nombre_producto}
              </Typography>
              <Box display="flex" alignItems="baseline" justifyContent="space-between">
                <Typography variant="h5" fontWeight="500">
                  {item.total_cantidad}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Unidades
                </Typography>
              </Box>
            </Box>
          </MetricCard>
        ))}
      </StyledContainer>
    </Box>
  )
}

const ResumenGarrafas = ({ resumen }) => {
  const theme = useTheme()

  const totalGeneral = resumen?.reduce((acc, item) => acc + (item.total_cantidad || 0), 0) || 0
  const envases = resumen?.filter((item) => Number(item.id_uso) === ID_USO_ENVASE) || []
  const conCarga = resumen?.filter((item) => Number(item.id_uso) !== ID_USO_ENVASE) || []

  if (!resumen?.length) {
    return (
      <Paper 
        variant="outlined" 
        sx={{ 
          p: 4, 
          textAlign: 'center',
          borderRadius: 2,
          bgcolor: alpha(theme.palette.common.black, 0.02)
        }}
      >
        <Typography color="textSecondary">
          No hay garrafas en esta rendición
        </Typography>
      </Paper>
    )
  }

  return (
    <Box>
      {/* Header minimalista */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="subtitle2" color="textSecondary">
          RESUMEN DE GARRAFAS
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {resumen.length} tipos
        </Typography>
      </Box>

      {/* Grid de productos separados por tipo */}
      <SeccionProductos titulo="Garrafas con carga" items={conCarga} />
      <SeccionProductos titulo="Envases" items={envases} />

      {/* Total minimalista */}
      <TotalCard elevation={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="textSecondary">
            Total general
          </Typography>
          <Box display="flex" alignItems="baseline" gap={1}>
            <Typography variant="h5" fontWeight="600" color="primary.main">
              {totalGeneral}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              unidades
            </Typography>
          </Box>
        </Box>
      </TotalCard>
    </Box>
  )
}

export default ResumenGarrafas